<!-- components/dashboard/MarketPrices.vue -->
<template>
  <el-card shadow="always" class="market-prices-card">
    <template #header>
      <!-- 标题左对齐，严格符合 v1.3 轻赛博朋克科技风格 -->
      <span class="title">💰 价格信息</span>
    </template>
    
    <el-row :gutter="16">
      <el-col 
        v-for="(price, index) in prices" 
        :key="price.name" 
        :span="6"
        class="price-postcard-col"
      >
        <div class="price-postcard">
          
          <!-- 明信片顶部标题 + 当前值 -->
          <div class="postcard-header">
            <span class="postcard-title">
              {{ price.name }}：{{ price.current_value }} {{ price.unit }}
            </span>
            <!-- 新增：标题下方日期（防止更新不及时） -->
            <span class="postcard-date">
              更新日期：{{ updateDate }}
            </span>
          </div>
          
          <!-- 上半部分：ECharts 折线图（近5日） -->
          <div class="chart-container">
            <div :ref="el => chartRefs[index] = el" class="echarts-box"></div>
          </div>
          
          <!-- 下半部分：表格 -->
          <el-table 
            :data="price.history" 
            :show-header="true"
            size="small"
            class="price-table"
            style="width: 100%;"
          >
            <el-table-column prop="date" label="日期" width="90" />
            <el-table-column prop="price1" :label="getPrice1Label(price.name)" />
            <el-table-column prop="price2" :label="getPrice2Label(price.name)" />
            <el-table-column prop="change" label="环比上涨" width="80">
              <template #default="{ row }">
                <span :class="['change-tag', row.change > 0 ? 'up' : row.change < 0 ? 'down' : 'flat']">
                  {{ row.change }}%
                </span>
              </template>
            </el-table-column>
          </el-table>
          
        </div>
      </el-col>
    </el-row>
  </el-card>
</template>

<script setup>
import { ref, onMounted, onUnmounted, nextTick } from 'vue'
import * as echarts from 'echarts'
import request from '@/utils/axios'

const prices = ref([])
// 存储每个明信片的 ECharts 实例引用
const chartRefs = ref([])
const charts = ref([])

// 统一显示的更新日期（所有明信片共用）
const updateDate = ref('')

const getPrice1Label = (name) => {
  if (name.includes('报价汇率')) return '当日汇率'
  if (name.includes('财务汇率')) return '当日汇率'
  if (name.includes('SMM长江铝锭价')) return '长江现货价'
  if (name.includes('盘条价格')) return '含税价格'
  return '价格1'
}

const getPrice2Label = (name) => {
  if (name.includes('报价汇率')) return '报价汇率'
  if (name.includes('财务汇率')) return '财务汇率'
  if (name.includes('SMM长江铝锭价')) return '伦敦LME价'
  if (name.includes('盘条价格')) return '不含税价格'
  return '价格2'
}

const loadPrices = async () => {
  try {
    const res = await request.get('/markets/current-prices/')
    const data = res?.data || res || []
    prices.value = data
  } catch (error) {
    console.error('[MarketPrices] 加载价格数据失败:', error)
  }
}

const initAllCharts = async () => {
  await nextTick()
  prices.value.forEach((price, index) => {
    const chartDom = chartRefs.value[index]
    if (!chartDom) return
    
    if (charts.value[index]) charts.value[index].dispose()
    
    const chart = echarts.init(chartDom)
    charts.value[index] = chart
    
    const p1Label = getPrice1Label(price.name)
    const p2Label = getPrice2Label(price.name)
    
    const option = {
      backgroundColor: 'transparent',
      grid: { left: 10, right: 10, top: 10, bottom: 20 },
      xAxis: {
        type: 'category',
        data: price.history.map(h => h.date),
        axisLabel: { color: '#606266', fontSize: 11, fontFamily: 'Courier New' }
      },
      yAxis: {
        type: 'value',
        axisLabel: { color: '#409eff', fontSize: 11, fontFamily: 'Courier New' }
      },
      tooltip: { trigger: 'axis' },
      series: [
        {
          name: p1Label,
          type: 'line',
          data: price.history.map(h => h.price1),
          smooth: true,
          lineStyle: { color: '#409eff', width: 2 },
          itemStyle: { color: '#409eff' }
        },
        {
          name: p2Label,
          type: 'line',
          data: price.history.map(h => h.price2),
          smooth: true,
          lineStyle: { color: '#67c23a', width: 2 },
          itemStyle: { color: '#67c23a' }
        }
      ]
    }
    
    chart.setOption(option)
  })
}

onMounted(() => {
  // 统一设置更新日期（格式：YYYY-MM-DD）
  const now = new Date()
  updateDate.value = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`
  
  loadPrices().then(() => {
    initAllCharts()
  })
})

onUnmounted(() => {
  charts.value.forEach(chart => {
    if (chart) chart.dispose()
  })
})
</script>

<style scoped>
.market-prices-card {
  height: 100%;
  border: 1px solid #409eff;
  box-shadow: 0 4px 20px rgba(64, 158, 255, 0.12);
  transition: all 0.3s ease;
}

.market-prices-card:hover {
  box-shadow: 0 6px 24px rgba(64, 158, 255, 0.18);
}

.title {
  font-size: 18px;
  font-weight: 600;
  color: #303133;
  letter-spacing: 1px;
  text-shadow: 0 0 6px rgba(64, 158, 255, 0.4);
}

.price-postcard {
  height: 100%;
  background: #fff;
  border-radius: 8px;
  border: 1px solid #e4e7ed;
  overflow: hidden;
  transition: all 0.3s ease;
  box-shadow: 0 2px 12px rgba(64, 158, 255, 0.08);
}

.price-postcard:hover {
  border-color: #409eff;
  box-shadow: 0 4px 20px rgba(64, 158, 255, 0.15);
}

/* 明信片顶部标题区域 */
.postcard-header {
  padding: 8px 12px 4px;
}

.postcard-title {
  font-size: 15px;
  font-weight: 700;
  color: #303133;
  letter-spacing: 1px;
  text-shadow: 0 0 6px rgba(64, 158, 255, 0.3);
  font-family: 'Courier New', 'Lucida Console', monospace;
  display: block;
}

.postcard-date {
  /* 日期样式：更小、更淡，左对齐 */
  font-size: 13px;
  color: #909399;
  font-family: 'Courier New', Courier, monospace;
  display: block;
  margin-top: 2px;
}

.chart-container {
  height: 160px;
  padding: 10px;
  background: linear-gradient(180deg, #f8fbff 0%, #f0f7ff 100%);
}

.echarts-box {
  width: 100%;
  height: 100%;
}

.price-table {
  --el-table-header-bg-color: #f5f7fa;
}

.change-tag {
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  font-family: 'Courier New', Courier, monospace;
}
.change-tag.up { background: #f0f9eb; color: #67c23a; }
.change-tag.down { background: #fef0f0; color: #f56c6c; }
.change-tag.flat { background: #f0f4ff; color: #409eff; }

.price-postcard-col {
  margin-bottom: 16px;
}
</style>